import { Dictionary } from "~/Types/Common";
import { stringify } from "qs";
import { getAccessToken } from "~/Services/Authentication";

export function get<T>(endpoint: string, parameter?: Dictionary<any>) {
    let queryString = stringify(parameter || {}, { encode: false });

    if (queryString !== "") {
        queryString = (endpoint.indexOf("?") < 0 ? "?" : "&") + queryString;
    }

    return requestJson<T>(endpoint + queryString, "GET");
}

export function post<T = void>(endpoint: string, model?: Parameters<(typeof JSON)["stringify"]>[0]) {
    return requestJson<T>(
        endpoint,
        "POST",
        { "Content-Type": "application/json" },
        model ? JSON.stringify(model) : undefined,
    );
}

export function postFormData<T = void>(endpoint: string, model?: FormData) {
    return request<T>(endpoint, "POST", {}, model);
}

export function deleteEntity<T = void>(endpoint: string, id: string) {
    return requestJson<T>(endpoint + "(" + id + ")", "DELETE", { "Content-Type": "application/json" }, undefined);
}

export function patch<T = void>(endpoint: string, model?: Parameters<(typeof JSON)["stringify"]>[0]) {
    return requestJson<T>(
        endpoint,
        "PATCH",
        { "Content-Type": "application/json" },
        model ? JSON.stringify(model) : undefined,
    );
}

export const postVoid = (endpoint: string, model?: any) =>
    requestVoid(endpoint, "POST", { "Content-Type": "application/json" }, model ? JSON.stringify(model) : undefined);

export function requestJson<T>(
    endpoint: string,
    method: string,
    headers: Dictionary<string> = {},
    body?: string | FormData,
) {
    return request<T>(`${endpoint}`, method, headers, body);
}

export async function request<T>(
    endpoint: string,
    method: string,
    headers: Dictionary<string> = { "X-Requested-With": "XMLHttpRequest" },
    body?: string | FormData,
    isStatusOkay?: (status: number) => boolean,
    cache: RequestInit["cache"] = "no-cache",
) {
    const response = await rawRequest(endpoint, method, headers, body, isStatusOkay, cache);

    try {
        return (await response.json()) as Promise<T>;
    } catch {
        return {} as Promise<T>;
    }
}

export async function requestVoid(
    endpoint: string,
    method: string,
    headers: Dictionary<string> = {},
    body?: string,
    isStatusOkay?: (status: number) => boolean,
) {
    await rawRequest(endpoint, method, headers, body, isStatusOkay ?? (status => status === 204));
}

export const rawRequest = async (
    endpoint: string,
    method = "GET",
    headers: Dictionary<string> = {},
    body?: string | FormData,
    isStatusOkay: (status: number) => boolean = status => status >= 200 && status < 300,
    cache: RequestInit["cache"] = "no-cache",
) => {
    const requestInit: RequestInit = {
        method,
        headers: headers as any,
        cache,
    };

    const accessToken = getAccessToken();
    if (accessToken) {
        (requestInit.headers as any)["Authorization"] = "Bearer " + accessToken;
    }

    if (body) {
        requestInit.body = body;
    }

    const response = await fetch(endpoint, requestInit);
    if (!isStatusOkay(response.status)) {
        let message: string;
        let errorJson: any;
        if (response.headers.get("Content-Type")?.startsWith("application/json")) {
            errorJson = (await response.json()) as {
                message: string;
                exceptionMessage?: string;
            };
            message = errorJson.exceptionMessage ?? errorJson.message;
        } else {
            message = await response.text();
        }
        // Some requests will occur during identity silent renew which will cause 401 responses until the new token comes back.
        // If we throw then error pop up will appear which we don't want
        if (response.status !== 401) {
            throw new ApiError(endpoint, response, message, errorJson);
        }
    }

    return response;
};

export class ApiError extends Error {
    constructor(url: string, response: Response, errorMessage: string, errorJson: any) {
        /** Node adds additional data to the `Response` type from `fetch`. */
        type PossibleNodeResponse = Response & {
            _raw?: unknown;
        };

        const { body, _raw, ...responseToLog } = response as PossibleNodeResponse;
        let message: any = {
            title: `Request to ${url} resulted in a error status ${response.status}.`,
            url: url,
            status: response.status,
            errorMessage: errorMessage,
            errorJson: errorJson,
            responseLog: responseToLog,
        };
        message = JSON.stringify(message);

        super(message);
    }
}
